import { orderItemStageId, refetchOrderItemStages, relocateOrderItemInCache, mergeOrderItemRow } from "@/composables/useOrderItems";
import { patchPrinterInCache } from "@/composables/usePrinters";
import { patchPrintJobInCache } from "@/composables/usePrintJobs";
import { Printer } from "@/interfaces";
import { OrderItem } from "@/types";
import { getSocket } from "@/utils/http-common";
import { useQueryClient } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import { Socket } from "socket.io-client";
import { useMainStore } from "./main.store";
import { useOrdersStore } from "./orders.store";
import { useFilesStore } from "./files.store";
import { previewFileId, serverPreviewState } from "@/utils/previewBlobs";

export const useWebSocketsStore = defineStore("WebSockets", {
  state: () => ({
    socket: null as Socket | null,
  }),
  getters: {},
  actions: {
    startSockets() {
      if (this.socket) {
        console.log("Socket connection already exists");
        return;
      }

      // Get queryClient in the proper injection context (before socket event handlers)
      const queryClient = useQueryClient();
      const socket = getSocket();
      this.socket = socket;

      socket.on("connect", () => {
        console.log("Socket connected for general events");
        // After successful connection, authenticate with token
        const store = useMainStore();
        socket.emit("authenticate", store.userToken);
      });

      socket.on("authenticated", () => {
        console.log("Socket authenticated successfully");
      });
      socket.on("printer", (printer: Printer) => {
        patchPrinterInCache(queryClient, printer);
      });
      socket.on("printJob", (data: any) => {
        // Handle both formats: direct printJob object and { action, entity, id } format
        const printJob = data.entity || data;
        const action = data.action;

        // If the entity doesn't have an ID but the data object does, use it
        if (!printJob.id && data.id) {
          printJob.id = data.id;
        }

        patchPrintJobInCache(queryClient, printJob, action);
      });
      socket.on("entityUpdate", (orderItem: OrderItem) => {
        const ordersStore = useOrdersStore();
        const previewState = serverPreviewState(orderItem.currentFile);
        const readyPreviewId = previewState === "ready" ? previewFileId(orderItem.currentFile) : undefined;
        if (readyPreviewId) useFilesStore().invalidatePreviewUrls([readyPreviewId]);
        const previous = orderItem.id != null ? ordersStore.orderItemsByStage.get(orderItem.id) : undefined;
        const merged = previous ? mergeOrderItemRow(previous, { ...orderItem, currentFile: orderItem.currentFile ?? previous.currentFile }) : orderItem;
        // Table view keeps rows in the vue-query cache, not orderItemsByStage.
        // Read the stage from that cache when the pinia map has no previous row.
        const { previousStageId, nextStageId, didMove } = relocateOrderItemInCache(queryClient, merged, {
          currentStageId: orderItemStageId(previous),
        });
        if (orderItem.id != null) ordersStore.setOrderItemByStage(orderItem.id, nextStageId != null ? { ...merged, workflowStageId: nextStageId } : merged);

        // The socket payload already patches the row. Reloading the stage is only needed
        // when the item leaves one list and joins another.
        if (didMove) {
          void refetchOrderItemStages(queryClient, [previousStageId, nextStageId]);
        }
        if (didMove && previousStageId != null && nextStageId != null) {
          const totals = new Map(ordersStore.totalOrderItems);
          totals.set(previousStageId, Math.max(0, (totals.get(previousStageId) ?? 0) - 1));
          totals.set(nextStageId, (totals.get(nextStageId) ?? 0) + 1);
          ordersStore.totalOrderItems = totals;
        }
        ordersStore.selectedItemsForTable = ordersStore.selectedItemsForTable.map((item) => (item.id === orderItem.id ? mergeOrderItemRow(item, orderItem) : item));
      });

      socket.on("incomingUpdate", () => {
        const ordersStore = useOrdersStore();
        ordersStore.fetchIncomingOrderItems();
      });

      socket.on("remove", (orderItem: OrderItem) => {
        const ordersStore = useOrdersStore();
        ordersStore.deleteOrderItemByStage(orderItem.id!);
        ordersStore.deleteOrderItem(orderItem);
      });

      socket.on("disconnect", () => {
        console.log("Socket disconnected from general events");
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
      });

      socket.on("error", (error) => {
        console.error("Socket error:", error);
      });

      socket.connect();
    },
  },
});
